import type { ReactNode } from 'react';

import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Box } from '@mui/material';
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';

import Typography from '@/components/Typography';

interface AccordionSectionProps {
  icon: ReactNode;
  title: string;
  children?: ReactNode;
  expanded?: boolean;
  onExpandedChange?: (expanded: boolean) => void;
}

const accordionSx = {
  backgroundColor: 'transparent',
  borderBottomWidth: 1,
  borderColor: 'rgba(248, 248, 248, 0.20)',
  borderBottomStyle: 'solid',
  width: '100%',
  p: 0
};

const summarySx = { p: 0, mt: 0 };
const titleRowSx = { display: 'flex', alignItems: 'center', gap: 0.5 };
const titleTextSx = { fontWeight: 700 };
const detailsSx = { p: 0, pb: 2 };

const slotProps = { transition: { mountOnEnter: true } } as const;

const AccordionSection = ({
  icon,
  title,
  children,
  expanded,
  onExpandedChange
}: AccordionSectionProps) => {
  return (
    <Accordion
      expanded={expanded}
      slotProps={slotProps}
      sx={accordionSx}
      onChange={
        onExpandedChange
          ? (_, value) => {
              onExpandedChange(value);
            }
          : undefined
      }
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        sx={summarySx}
      >
        <Box sx={titleRowSx}>
          <Box
            sx={theme => ({
              display: 'flex',
              alignItems: 'center',
              fontSize: theme.typography.pxToRem(16)
            })}
          >
            {icon}
          </Box>
          <Typography
            component='span'
            size={16}
            sx={titleTextSx}
          >
            {title}
          </Typography>
        </Box>
      </AccordionSummary>
      <AccordionDetails sx={detailsSx}>{children}</AccordionDetails>
    </Accordion>
  );
};

export default AccordionSection;
